import * as React from 'react';
import { waitForMicrofrontendApi } from 'shared/common/utils/microfrontend';

import { TContext, TSaveSearchParams } from '../../types/context';
import { TFetchManifestResult } from '../../types/microfrontend';
import { ISaveSearchModalApi } from '../../types/saveSearchModal';
import { context as Context } from '../../utils/context';

import type { ICianMFUIElement } from '@cian/mf-registry/browser';
import type { IMicrofrontendManifest } from '@cian/mf-registry/shared';

interface IProviderProps {
  fetchManifest: () => Promise<TFetchManifestResult>;
  params: TSaveSearchParams;
}

export const OPEN_MODAL = '/v1/open-modal';
export const CLOSE_MODAL = '/v1/close-modal';

export const Provider: React.FC<React.PropsWithChildren<IProviderProps>> = ({ children, fetchManifest, params }) => {
  const microfrontendRef = React.useRef<ICianMFUIElement | null>(null);
  const [manifest, setManifest] = React.useState<IMicrofrontendManifest | null>(null);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleOpen = React.useCallback(async () => {
    setOpen(true);
    setFailed(false);

    if (!manifest) {
      setLoading(true);
      const result = await fetchManifest();

      if (!result) {
        setLoading(false);

        return;
      }

      // Микрофронтенд начинает монтироваться, но статус остаётся loading:
      // пока он не готов, в фолбэк-модалке должен крутиться лоадер.
      setManifest(result.manifest);
    }

    const api = await waitForMicrofrontendApi<ISaveSearchModalApi>(microfrontendRef);

    if (!api) {
      setFailed(true);
      setLoading(false);

      return;
    }

    api[OPEN_MODAL]();
    setLoading(false);
  }, [fetchManifest, manifest]);

  const context = React.useMemo<TContext>(() => {
    const commonParameters = {
      microfrontendRef,
      params,
      open,
      // setParams,
      onOpen: handleOpen,
      onClose: handleClose,
      manifest: manifest ?? undefined,
    };

    if (loading) {
      return {
        ...commonParameters,
        status: 'loading',
      };
    }

    if (failed || !manifest) {
      return {
        ...commonParameters,
        status: 'failed',
      };
    }

    return {
      ...commonParameters,
      manifest,
      status: 'succeed',
    };
  }, [open, handleOpen, handleClose, loading, failed, manifest, params]);

  return <Context.Provider value={context}>{children}</Context.Provider>;
};
